import { emit, events } from 'elc-service-bus';
import { observable, computed, action, when } from 'mobx';
import { ConfigStore } from '../../service-setup/ConfigStore';
import { ProductRepository } from '../repositories/ProductRepository';
import * as Constants from '../constants/PowerReviews';
import { IRatingsReviewsConfig } from '../interfaces/IRating';
import { RatingsLoadedUseCase } from '../use-cases/RatingsLoadedUseCase';
import { IProductReviews } from '../../exported/product-reviews/ProductReviews';
import { RatingsReviewsBaseController } from './RatingsReviewsBaseController';
import { REVIEW_CATEGORY } from '../constants/ProductReview';

export class ProductReviewsViewController extends RatingsReviewsBaseController {
    @observable public isLoading: boolean = true;
    @observable public config: IRatingsReviewsConfig;
    @observable public productRepository: ProductRepository;
    @observable public powerReviewsScript: HTMLScriptElement;
    @observable public activeTab: string = 'tab-1';

    constructor(configStore: ConfigStore, productRepository: ProductRepository) {
        super();
        this.config = configStore.config;
        this.productRepository = productRepository;

        when(
            () => this.noReviews,
            // tslint:disable-next-line: no-void-expression
            () => this.showReviewsOnViewportIntersection()
        );
    }

    public initialize(props: IProductReviews) {
        const { config } = props;
        this.config = { ...this.config, ...config };
    }

    @computed
    get data() {
        return {
            isLoading: this.isLoading,
            activeTab: this.activeTab
        };
    }

    @computed
    public get productId() {
        return this.productRepository.productId || this.config.productId;
    }

    @computed
    public get product() {
        return this.productRepository.getProductById(this.productId);
    }

    @computed
    public get numberOfReviewsValue(): number {
        return this.product && this.product.reviews && this.product.reviews.numberOfReviews;
    }

    @computed
    public get noReviews(): boolean {
        const reviewsValue = this.numberOfReviewsValue === null || this.numberOfReviewsValue === 0;

        return Boolean(reviewsValue);
    }

    @action
    public setPaginationType = (paginationType: string) => {
        this.productRepository.setPaginationType(paginationType);
    };

    @action
    public onTabClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = <HTMLElement>event.target;
        this.activeTab = target.dataset.tab || '';
    };

    @action
    public loadReviews = (powerReviewsScript: HTMLScriptElement) => {
        this.isLoading = false;
        // tslint:disable-next-line: no-floating-promises
        this.loaded().then(() => {
            this.loadPowerReviewsScripts(powerReviewsScript);
            this.addReviewsEventHandlers({
                id: this.productId,
                name: this.product.displayName,
                reviewCategory: REVIEW_CATEGORY
            });
        });
    };

    public handleProductReviewsUnmount = () => {
        this.removeReviewsEventHandlers();
    };

    @action
    public loadPowerReviewsScripts = (powerReviewsScript: HTMLScriptElement) => {
        const bodyFirstScript = <HTMLScriptElement>document.querySelector('body > script');
        const parentNode = <HTMLScriptElement>bodyFirstScript.parentNode;
        powerReviewsScript.type = 'text/javascript';
        powerReviewsScript.src = Constants.POWER_REVIEWS_SCRIPT;
        powerReviewsScript.defer = true;
        powerReviewsScript.async = true;
        powerReviewsScript.onload = () => {
            this.emitReviewsInit();
        };
        parentNode.insertBefore(powerReviewsScript, bodyFirstScript.nextSibling);

        this.powerReviewsScript = powerReviewsScript;
    };

    public showReviewsOnViewportIntersection = () => {
        const reviewsWrapper = document.querySelector('.elc-product-reviews');
        const prScript = document.createElement('script');

        const intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.intersectionRatio > 0 && entry.isIntersecting) {
                    if (this.powerReviewsScript && this.powerReviewsScript.src) {
                        intersectionObserver.disconnect();
                        if (reviewsWrapper) {
                            intersectionObserver.unobserve(reviewsWrapper);
                        }
                    } else {
                        this.loadReviews(prScript);
                    }
                } else {
                    if (this.powerReviewsScript && this.powerReviewsScript.src) {
                        intersectionObserver.disconnect();
                        if (reviewsWrapper) {
                            intersectionObserver.unobserve(reviewsWrapper);
                        }
                    } else if (this.noReviews) {
                        this.loadReviews(prScript);
                    }
                }
            },
            {
                root: null,
                rootMargin: '300px',
                threshold: 0.1
            }
        );

        if (reviewsWrapper) {
            intersectionObserver.observe(reviewsWrapper);
        }
    };

    public loaded = async () => {
        if (this.productId) {
            const ratingsLoadedUseCase = new RatingsLoadedUseCase(this.productId);
            await ratingsLoadedUseCase.execute();
        }

        return Promise.resolve();
    };

    public emitReviewsInit = () => {
        emit(events.SPP_POWER_REVIEWS_INIT, this.productId);
    };
}
